import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, X, Check, Pencil, GripVertical } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export const ServicesManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [dragIndex, setDragIndex] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/services");
      setItems(data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) { toast.error("Please enter a service name."); return; }
    setSaving(true);
    try {
      await api.post("/admin/services", { title: newTitle.trim(), desc: newDesc.trim() });
      toast.success("Service added");
      setNewTitle(""); setNewDesc(""); setAdding(false);
      load();
    } catch {
      toast.error("Couldn't add. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (id) => {
    if (!editTitle.trim()) { toast.error("Name can't be empty."); return; }
    try {
      await api.put(`/admin/services/${id}`, { title: editTitle.trim(), desc: editDesc.trim() });
      setItems((prev) => prev.map((s) => (s.id === id ? { ...s, title: editTitle.trim(), desc: editDesc.trim() } : s)));
      setEditId(null);
      toast.success("Service updated");
    } catch {
      toast.error("Couldn't update.");
    }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Remove the "${title}" service?`)) return;
    const prev = items;
    setItems((s) => s.filter((x) => x.id !== id));
    try {
      await api.delete(`/admin/services/${id}`);
      toast.success("Service removed");
    } catch {
      toast.error("Couldn't remove.");
      setItems(prev);
    }
  };

  const persistOrder = async (newItems) => {
    setItems(newItems);
    try {
      await api.put("/admin/services-reorder", { ordered_ids: newItems.map((i) => i.id) });
      toast.success("Order saved");
    } catch {
      toast.error("Couldn't save order.");
      load();
    }
  };

  const handleDrop = (dropIndex) => {
    if (dragIndex === null || dragIndex === dropIndex) { setDragIndex(null); return; }
    const reordered = [...items];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    setDragIndex(null);
    persistOrder(reordered);
  };

  return (
    <div data-testid="services-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-navy/60 text-sm tracking-[0.1em]">{items.length} SERVICE{items.length === 1 ? "" : "S"}</p>
          {items.length > 1 && <p className="text-navy/40 text-xs mt-1 flex items-center gap-1.5"><GripVertical size={12} /> Drag the handle to reorder</p>}
        </div>
        {!adding && (
          <button onClick={() => setAdding(true)} data-testid="add-service-button"
            className="flex items-center gap-2 bg-ink text-[#f2f0ea] px-5 py-2.5 text-xs font-bold tracking-[0.14em] hover:bg-navy transition-colors">
            <Plus size={15} /> ADD SERVICE
          </button>
        )}
      </div>

      {adding && (
        <form onSubmit={add} data-testid="add-service-form" className="bg-white border border-line p-6 mb-8 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-display text-lg tracking-[0.04em]">Add new service</h4>
            <button type="button" onClick={() => { setAdding(false); setNewTitle(""); setNewDesc(""); }} className="text-navy/50 hover:text-navy"><X size={18} /></button>
          </div>
          <div>
            <label className="block text-xs tracking-[0.14em] text-navy/60 font-semibold mb-1.5">SERVICE NAME</label>
            <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} data-testid="service-title-input"
              className="w-full border border-line px-4 py-3 text-sm outline-none focus:border-navy" placeholder="e.g. Heat Transfer Printing" />
          </div>
          <div>
            <label className="block text-xs tracking-[0.14em] text-navy/60 font-semibold mb-1.5">DESCRIPTION</label>
            <textarea value={newDesc} onChange={(e) => setNewDesc(e.target.value)} rows={2} data-testid="service-desc-input"
              className="w-full border border-line px-4 py-3 text-sm outline-none focus:border-navy resize-none" placeholder="A short sentence describing this service." />
          </div>
          <button type="submit" disabled={saving} data-testid="submit-service-button"
            className="flex items-center gap-2 bg-gold text-ink px-6 py-3 text-xs font-bold tracking-[0.14em] hover:bg-gold-soft transition-colors disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
            {saving ? "SAVING…" : "SAVE"}
          </button>
        </form>
      )}

      {loading ? (
        <p className="text-navy/50">Loading…</p>
      ) : items.length === 0 ? (
        <div className="bg-white border border-line p-12 text-center text-navy/60">No services yet. Add your first one.</div>
      ) : (
        <div className="space-y-3">
          {items.map((s, idx) => (
            <div key={s.id}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(idx)}
              className={`bg-white border p-5 flex items-start gap-4 transition-all ${dragIndex === idx ? "opacity-40 border-gold" : "border-line"}`}
              data-testid={`service-item-${s.id}`}>
              <div
                draggable
                onDragStart={() => setDragIndex(idx)}
                onDragEnd={() => setDragIndex(null)}
                data-testid={`service-drag-${s.id}`}
                className="text-navy/30 hover:text-navy cursor-grab active:cursor-grabbing pt-1"
                title="Drag to reorder">
                <GripVertical size={18} />
              </div>

              {editId === s.id ? (
                <div className="flex-1 space-y-2">
                  <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} data-testid={`edit-service-title-${s.id}`}
                    className="w-full border border-line px-3 py-2 text-sm outline-none focus:border-navy font-semibold" />
                  <textarea value={editDesc} onChange={(e) => setEditDesc(e.target.value)} rows={2} data-testid={`edit-service-desc-${s.id}`}
                    className="w-full border border-line px-3 py-2 text-sm outline-none focus:border-navy resize-none" />
                  <div className="flex gap-2">
                    <button onClick={() => saveEdit(s.id)} data-testid={`save-service-${s.id}`} className="flex items-center gap-1.5 bg-ink text-white px-4 py-2 text-xs font-bold tracking-[0.1em]"><Check size={14} /> SAVE</button>
                    <button onClick={() => setEditId(null)} className="flex items-center gap-1.5 border border-line px-4 py-2 text-xs font-bold tracking-[0.1em] text-navy"><X size={14} /> CANCEL</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex-1">
                    <div className="font-display text-lg tracking-[0.03em]">{s.title}</div>
                    <p className="text-navy/60 text-sm mt-1">{s.desc}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <button onClick={() => { setEditId(s.id); setEditTitle(s.title); setEditDesc(s.desc || ""); }} data-testid={`edit-service-${s.id}`} className="text-navy/40 hover:text-gold"><Pencil size={15} /></button>
                    <button onClick={() => remove(s.id, s.title)} data-testid={`delete-service-${s.id}`} className="text-red-600 hover:text-red-700"><Trash2 size={15} /></button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
